import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import {
  Button,
  Alert,
  Spinner,
  Form,
  FormGroup,
  Label,
  Input,
} from '../shim/reactstrap';
import { useAuth } from '../contexts/AuthContext';

// Edit page for a single Trading Strategy.
// When the URL is /trading-strategies/new, a new strategy is created on save.
// When the URL is /trading-strategies/:id/edit, the existing strategy is loaded and updated.
//
// Layout intentionally mirrors TradingStrategyView: a plain page header with action
// buttons and a single-column details/form table, no centered card chrome.
//
// Portfolio construction (added 2026-06):
//   - Money Market Ticker (optional AsyncSelect over the broker's catalog)
//   - Stocks list — each committed row is a readonly Symbol + editable Share
//     input. Only the LAST row (the one being filled in) shows the ticker
//     picker. To add a new row, type a symbol into the picker, set a share,
//     and click "+ Add" — the row commits, and a fresh picker row appears
//     underneath.
export const TradingStrategyEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiClient, user, isAuthenticated } = useAuth();

  // EnableDebugging is admin-only. The backend will silently force the flag
  // back to false for non-admin callers, but we hide the toggle entirely so
  // the UI matches what the user is actually allowed to change. This avoids
  // surprising the user with a checkbox that flips on save without effect.
  const isAdmin = isAuthenticated && !!user?.Roles?.includes('Admin');

  const isCreating = !id;

  const [loading, setLoading] = useState(!isCreating);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');

  // Form state
  const [editTradingConnectorId, setEditTradingConnectorId] = useState('');
  const [editModelId, setEditModelId] = useState('');
  const [editName, setEditName] = useState('');
  const [editEnabled, setEditEnabled] = useState(false);
  const [editRequireTradeConfirmation, setEditRequireTradeConfirmation] = useState(true);
  const [editTradeAmountUsd, setEditTradeAmountUsd] = useState('');
  const [editAdjustTradeAmountAutomatically, setEditAdjustTradeAmountAutomatically] = useState(false);
  const [editEnableDebugging, setEditEnableDebugging] = useState(false);
  // Order type the executor posts when the strategy fires. Mirrors
  // DAL.TradingOrderType on the server ("MarketOnClose" / "Market").
  // Defaulted to "MarketOnClose" — matches the historical behaviour and
  // the DB column's NOT NULL DEFAULT, so a user who never touches this
  // picker continues to get MOC orders.
  const [editOrderType, setEditOrderType] = useState('MarketOnClose');
  // Time of execution in Eastern time, expressed as minutes from midnight.
  // Valid range: 570 (09:30 ET, market open) to 930 (15:30 ET, 30 min
  // before close). Default matches the historical scheduler trigger.
  const [editExecutionTimeMinutes, setEditExecutionTimeMinutes] = useState(930);
  const [saving, setSaving] = useState(false);

  const [connectors, setConnectors] = useState([]);
  const [models, setModels] = useState([]);

  // SignalTag is the tag this strategy trades on. The picker is driven
  // by the SELECTED model's SignalTags list — when the user changes the
  // model we reset the tag so a stale value can never leak into the
  // request (the server rejects mismatches against the new model's
  // tags, but failing client-side saves the round-trip).
  const [editSignalTag, setEditSignalTag] = useState('');

  // Portfolio-config state.
  //   - `moneyMarketSymbol` is a plain string (the broker symbol), not an
  //     AsyncSelect option. The picker option lives in `moneyMarketOption`.
  //   - `stocks` is `[{ symbol: string|null, percentage: number }]`. The LAST
  //     row is always the picker row (symbol === null) — committing a symbol
  //     pushes the next row into the picker slot.
  const [moneyMarketSymbol, setMoneyMarketSymbol] = useState(null);
  const [moneyMarketOption, setMoneyMarketOption] = useState(null);
  const [stocks, setStocks] = useState([emptyStockRow()]);
  const [equalShare, setEqualShare] = useState(true);

  // When the user changes the connector, drop any picker state that belongs
  // to the previous broker.
  const [activeConnectorId, setActiveConnectorId] = useState(null);
  const catalogSeqRef = useRef(0);

  const loadLookups = useCallback(async () => {
    try {
      const [connectorsData, modelsData] = await Promise.all([
        apiClient.getTradingConnectors(),
        apiClient.getModelsList(),
      ]);
      setConnectors(connectorsData || []);
      setModels(modelsData || []);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load connectors or models.');
    }
  }, [apiClient]);

  const loadStrategy = useCallback(async (strategyId) => {
    setLoadError('');
    try {
      const s = await apiClient.getTradingStrategy(strategyId);
      setEditTradingConnectorId(String(s.TradingConnectorId));
      setEditModelId(String(s.ModelId));
      setEditSignalTag(s.SignalTag || '');
      setEditName(s.Name || '');
      setEditEnabled(!!s.Enabled);
      setEditRequireTradeConfirmation(s.RequireTradeConfirmation !== false);
      // TradeAmountUsd can be null (use all available cash) or a positive number.
      setEditTradeAmountUsd(s.TradeAmountUsd === null || s.TradeAmountUsd === undefined
        ? ''
        : String(s.TradeAmountUsd));
      setEditAdjustTradeAmountAutomatically(!!s.AdjustTradeAmountAutomatically);
      setEditEnableDebugging(!!s.EnableDebugging);
      // OrderType: server stores the enum name as a string. Fall back
      // to the historical MOC default when the field is missing (defensive
      // against legacy payloads — every API response since
      // Migration_076 actually carries the column).
      setEditOrderType(s.OrderType === 'Market' ? 'Market' : 'MarketOnClose');
      // ExecutionTime: server sends "HH:mm:ss" (Newtonsoft TimeSpan format).
      // Parse it back to integer minutes for the slider.
      // Fall back to 930 (15:30 ET) for legacy responses.
      setEditExecutionTimeMinutes(timeSpanStringToMinutes(s.ExecutionTime, 930));
      setActiveConnectorId(s.TradingConnectorId);

      // Money-market: persist the raw symbol (backend dropped the FK).
      if (s.MoneyMarketSymbol) {
        setMoneyMarketSymbol(s.MoneyMarketSymbol);
        setMoneyMarketOption({
          value: s.MoneyMarketSymbol,
          label: s.MoneyMarketSymbol,
        });
      } else {
        setMoneyMarketSymbol(null);
        setMoneyMarketOption(null);
      }

      // Stocks: every persisted row becomes a committed readonly row, then we
      // append one empty picker row at the bottom for further additions.
      const persisted = (s.Stocks || [])
        .filter((row) => row && row.Symbol)
        .map((row) => ({
          symbol: row.Symbol,
          percentage: Number(row.Percentage) || 0,
        }));
      setStocks(persisted.length > 0 ? [...persisted, emptyStockRow()] : [emptyStockRow()]);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load trading strategy.');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    loadLookups();
    if (!isCreating) {
      loadStrategy(id);
    } else {
      setLoading(false);
    }
  }, [id, isCreating, loadLookups, loadStrategy]);

  // ── Catalog loaders (connector-relative) ────────────────────────────────
  // The same broker catalog feeds both the money-market picker and the
  // stock-row picker. `defaultOptions: true` lets the user see the full
  // catalog immediately for the money market (where there are only a handful
  // of typical tickers like BIL/SGOV); the stock picker uses search-only.
  const loadConnectorTickerOptions = useCallback(
    async (inputValue, callback) => {
      if (!activeConnectorId) {
        callback([]);
        return;
      }
      const seq = ++catalogSeqRef.current;
      try {
        const data = await apiClient.getTradingConnectorTickers(activeConnectorId, {
          search: inputValue || '',
          limit: 20,
        });
        if (seq !== catalogSeqRef.current) return; // stale response
        const options = (data || []).map((t) => ({
          value: t.Symbol,
          label: t.Name ? `${t.Symbol} — ${t.Name}` : t.Symbol,
        }));
        callback(options);
      } catch (err) {
        if (seq === catalogSeqRef.current) callback([]);
      }
    },
    [apiClient, activeConnectorId]
  );

  const loadMoneyMarketOptions = useCallback(
    async (inputValue, callback) => {
      if (!activeConnectorId) {
        callback([]);
        return;
      }
      const seq = ++catalogSeqRef.current;
      try {
        const data = await apiClient.getTradingConnectorTickers(activeConnectorId, {
          search: inputValue || '',
          limit: 20,
        });
        if (seq !== catalogSeqRef.current) return;
        const options = (data || []).map((t) => ({
          value: t.Symbol,
          label: t.Name ? `${t.Symbol} — ${t.Name}` : t.Symbol,
        }));
        callback(options);
      } catch (err) {
        if (seq === catalogSeqRef.current) callback([]);
      }
    },
    [apiClient, activeConnectorId]
  );

  // ── Stocks list helpers ─────────────────────────────────────────────────
  const committedStocks = useMemo(
    () => stocks.filter((r) => r.symbol),
    [stocks]
  );
  const percentSum = useMemo(
    () => committedStocks.reduce((acc, s) => acc + (Number(s.percentage) || 0), 0),
    [committedStocks]
  );

  const updateStock = (idx, patch) => {
    setStocks((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  // Redistribution helper used by commit/remove/toggle-equal-share. Walks
  // `rows` in order, distributes 100% evenly across the committed (non-empty)
  //  rows, leaves any trailing empty picker rows untouched, and parks the
  // rounding remainder on the LAST committed row so the sum stays exactly
  // at 100 within rounding error.
  const redistributeEqualShare = (rows) => {
    const committedIndices = rows
      .map((r, i) => (r.symbol ? i : -1))
      .filter((i) => i >= 0);

    if (committedIndices.length === 0) return rows;

    const each = +(100 / committedIndices.length).toFixed(2);
    const adjusted = each * (committedIndices.length - 1);
    const lastCommittedIndex = committedIndices[committedIndices.length - 1];

    return rows.map((r, i) => {
      if (!r.symbol) return r;
      return {
        ...r,
        percentage: i === lastCommittedIndex ? +(100 - adjusted).toFixed(2) : each,
      };
    });
  };

  // Commit the picker row (the last row). The new symbol locks in; a fresh
  // empty picker row is appended below. When "Equal share" is on, the new
  // row (and any previously committed rows) are redistributed across
  // 100% immediately so the first stock doesn't sit at 0% just because
  // the user typed a symbol without touching the percentage input. The
  // trailing empty picker row is preserved so the user can keep adding
  // stocks (otherwise the second "+ Add" press would do nothing).
  const commitStockRow = (idx) => {
    setStocks((prev) => {
      if (idx !== prev.length - 1) return prev; // only the last row is the picker
      const row = prev[idx];
      if (!row.symbol) return prev; // nothing to commit
      const committed = { symbol: row.symbol, percentage: Number(row.percentage) || 0 };
      const next = [...prev.slice(0, idx), committed, emptyStockRow()];
      return equalShare ? redistributeEqualShare(next) : next;
    });
  };

  const removeStock = (idx) => {
    setStocks((prev) => {
      if (prev.length === 1) return prev; // keep at least the picker row
      const next = prev.filter((_, i) => i !== idx);
      // Keep the equal-share invariant in sync — removing one stock from a
      // 3-way equal split should redistribute the remaining 2 across 100%.
      // The trailing empty picker row is preserved as-is (no symbol).
      return equalShare ? redistributeEqualShare(next) : next;
    });
  };

  // Distributes 100% evenly across the given committed rows. Convenience
  // wrapper around redistributeEqualShare for callers that already pass only
  // committed rows (the Equal-share toggle handler, which filters first).
  const distributeEqually = (rows) => redistributeEqualShare(rows);

  const handleEqualShareChange = (checked) => {
    setEqualShare(checked);
    if (checked) {
      setStocks((prev) => distributeEqually(prev.filter((r) => r.symbol)));
    }
  };

  // When the connector changes, reset all picker state so the user can't keep
  // selections that belong to the previous broker.
  const handleConnectorChange = (newId) => {
    setEditTradingConnectorId(newId);
    const numeric = parseInt(newId, 10);
    if (numeric !== activeConnectorId) {
      setActiveConnectorId(Number.isFinite(numeric) ? numeric : null);
      setMoneyMarketSymbol(null);
      setMoneyMarketOption(null);
      setStocks([emptyStockRow()]);
    }
  };

  // When the model changes, the SignalTag picker has to follow — the new
  // model may not even expose the previous tag. If the previous tag is
  // still available on the new model we keep it (preserves intent), otherwise
  // we fall back to the first available tag (or empty when the model
  // exposes none, which the server then rejects — better than silently
  // keeping a stale tag).
  const handleModelChange = (newId) => {
    setEditModelId(newId);
    const numeric = parseInt(newId, 10);
    if (!Number.isFinite(numeric)) {
      setEditSignalTag('');
      return;
    }
    const newModel = (models || []).find((m) => String(m.Id) === String(numeric));
    const available = (newModel && Array.isArray(newModel.SignalTags)) ? newModel.SignalTags : [];
    if (available.includes(editSignalTag)) {
      return;
    }
    setEditSignalTag(available.length > 0 ? available[0] : '');
  };

  // Available tags for the currently selected model — used to populate
  // the SignalTag dropdown below.
  const selectedModel = useMemo(
    () => (models || []).find((m) => String(m.Id) === String(editModelId)) || null,
    [models, editModelId]
  );
  const availableSignalTags = (selectedModel && Array.isArray(selectedModel.SignalTags))
    ? selectedModel.SignalTags
    : [];

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setSaveError('');

    if (!editName.trim()) {
      setSaveError('Name is required.');
      return;
    }
    if (!editTradingConnectorId) {
      setSaveError('Please select a trading connector.');
      return;
    }
    if (!editModelId) {
      setSaveError('Please select a model.');
      return;
    }
    if (!editSignalTag.trim()) {
      setSaveError('Please select a signal tag.');
      return;
    }

    const body = {
      Name: editName.trim(),
      TradingConnectorId: parseInt(editTradingConnectorId, 10),
      ModelId: parseInt(editModelId, 10),
      SignalTag: editSignalTag.trim(),
      Enabled: !!editEnabled,
      RequireTradeConfirmation: !!editRequireTradeConfirmation,
      // TradeAmountUsd is optional — empty input means "use all available
      // cash" (the legacy behaviour). Only include the field when the user
      // typed a value so the backend distinguishes null vs 0 cleanly.
      TradeAmountUsd: editTradeAmountUsd === '' ? null : Number(editTradeAmountUsd),
      AdjustTradeAmountAutomatically: !!editAdjustTradeAmountAutomatically,
      // EnableDebugging is admin-only. Non-admin callers omit the field
      // entirely so the server leaves the persisted value untouched (the
      // request DTO is nullable — "not passed" means "do not update"). The
      // toggle is also hidden in the UI below for non-admin users.
      ...(isAdmin ? { EnableDebugging: !!editEnableDebugging } : {}),
      MoneyMarketSymbol: moneyMarketSymbol,
      // OrderType is always sent — there's no "leave alone" semantics on
      // create, and on update the picker reflects the user's choice
      // (defaulted to MarketOnClose on initial load). Sending both
      // values keeps the wire format simple.
      OrderType: editOrderType === 'Market' ? 'Market' : 'MarketOnClose',
      // ExecutionTime: sent as "HH:mm:ss" string — Newtonsoft.Json
      // deserialises that directly into TimeSpan on the server.
      ExecutionTime: minutesToTimeSpanString(editExecutionTimeMinutes),
      Stocks: committedStocks.map((r) => ({
        Symbol: r.symbol,
        Percentage: Number(r.percentage) || 0,
      })),
    };
    if (!isCreating) {
      body.Id = parseInt(id, 10);
    }

    setSaving(true);
    try {
      await apiClient.saveTradingStrategy(body);
      navigate('/trading-strategies');
    } catch (err) {
      setSaveError(err?.message || 'Failed to save trading strategy.');
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3" style={{ gap: '12px' }}>
        <h1 className="mb-0">
          {isCreating ? 'New Trading Strategy' : `Trading Strategy #${id}`}
        </h1>
        {!isCreating && (
          <Button color="primary" size="sm" tag={Link} to={`/trading-strategies/${id}/view`}>
            View
          </Button>
        )}
        <Button color="secondary" size="sm" tag={Link} to="/trading-strategies">
          Back
        </Button>
      </div>

      {loadError && <Alert color="danger">{loadError}</Alert>}
      {saveError && <Alert color="danger">{saveError}</Alert>}

      {loading && (
        <div className="text-center py-3"><Spinner /></div>
      )}

      {!loading && (
        <Form onSubmit={handleSubmit}>
          <table className="table" style={{ maxWidth: '960px' }}>
            <tbody>
              <tr>
                <th style={{ width: '220px' }}>Name</th>
                <td>
                  <Input
                    id="strategyName"
                    type="text"
                    bsSize="sm"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    disabled={saving}
                    maxLength={128}
                    placeholder="e.g. SPY Momentum Strategy"
                  />
                </td>
              </tr>
              <tr>
                <th>Trading Connector</th>
                <td>
                  <Input
                    id="strategyConnector"
                    type="select"
                    bsSize="sm"
                    value={editTradingConnectorId}
                    onChange={(e) => handleConnectorChange(e.target.value)}
                    disabled={saving}
                    required
                  >
                    <option value="">-- Select a connector --</option>
                    {connectors.map((c) => (
                      <option key={c.Id} value={c.Id}>
                        #{c.Id} {c.Name} [{c.getTypeDisplay()} / {c.getModeDisplay()}]
                      </option>
                    ))}
                  </Input>
                  {connectors.length === 0 && (
                    <small className="form-text text-muted">
                      You don't have any trading connectors yet. Create one in the
                      {' '}
                      <Link to="/trading-connectors">Trading Connectors</Link>
                      {' '}
                      page first.
                    </small>
                  )}
                </td>
              </tr>
              <tr>
                <th>Model</th>
                <td>
                  <Input
                    id="strategyModel"
                    type="select"
                    bsSize="sm"
                    value={editModelId}
                    onChange={(e) => handleModelChange(e.target.value)}
                    disabled={saving}
                    required
                  >
                    <option value="">-- Select a model --</option>
                    {models.map((m) => (
                      <option key={m.Id} value={m.Id}>
                        #{m.Id} — {m.Name || m.FileName}
                        {m.OwnerName ? ` (${m.OwnerName})` : ''}
                      </option>
                    ))}
                  </Input>
                </td>
              </tr>

              {/* SignalTag — the strategy must declare which of the model's
                  signal tags it trades on. The dropdown is rebuilt from the
                  selected model's SignalTags list so the user can never
                  pick a tag the model doesn't produce. */}
              <tr>
                <th>Signal Tag</th>
                <td>
                  <Input
                    id="strategySignalTag"
                    type="select"
                    bsSize="sm"
                    value={editSignalTag}
                    onChange={(e) => setEditSignalTag(e.target.value)}
                    disabled={saving || !editModelId}
                    required
                  >
                    {!editModelId && (
                      <option value="">-- Select a model first --</option>
                    )}
                    {editModelId && availableSignalTags.length === 0 && (
                      <option value="">-- No tags available for this model --</option>
                    )}
                    {availableSignalTags.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </Input>
                  {editModelId && availableSignalTags.length > 0 && (
                    <small className="form-text text-muted">
                      The strategy will read signals only from this tag's
                      signal file on every executor run.
                    </small>
                  )}
                </td>
              </tr>

              {/* Portfolio construction */}
              <tr>
                <th>Money Market Ticker</th>
                <td>
                  <AsyncSelect
                    key={`mm-${activeConnectorId || 'none'}`}
                    cacheOptions
                    loadOptions={loadMoneyMarketOptions}
                    value={moneyMarketOption}
                    onChange={(opt) => {
                      setMoneyMarketOption(opt);
                      setMoneyMarketSymbol(opt ? opt.value : null);
                    }}
                    placeholder={
                      activeConnectorId
                        ? 'Type to search money-market ticker (optional)...'
                        : 'Select a trading connector first'
                    }
                    isClearable
                    isDisabled={!activeConnectorId || saving}
                    defaultOptions={!!activeConnectorId}
                    noOptionsMessage={() =>
                      activeConnectorId ? 'No matches' : 'Select a connector first'
                    }
                  />
                  <small className="form-text text-muted">
                    Optional. When set, the strategy buys this ticker on a Money signal
                    instead of holding cash.
                  </small>
                </td>
              </tr>
              <tr>
                <th>
                  Stocks &nbsp;
                  <span className={`badge bg-${Math.abs(percentSum - 100) < 0.01 ? 'success' : 'danger'}`}>
                    {percentSum.toFixed(1)}%
                  </span>
                </th>
                <td>
                  <div className="d-flex justify-content-end mb-2">
                    <FormGroup check inline className="mb-0">
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={equalShare}
                          onChange={(e) => handleEqualShareChange(e.target.checked)}
                          disabled={saving}
                        />
                        {' '}Equal share
                      </Label>
                    </FormGroup>
                  </div>

                  {stocks.map((row, idx) => {
                    // The LAST row is always the picker, regardless of whether
                    // a symbol has been typed into it. Toggling isPickerRow on
                    // a freshly-picked symbol caused the "+ Add" button to
                    // disappear before the user could click it.
                    const isPickerRow = idx === stocks.length - 1;
                    return (
                      <div key={idx} className="d-flex gap-2 align-items-center mb-2">
                        <div style={{ flex: 1 }}>
                          {isPickerRow ? (
                            <AsyncSelect
                              key={`stock-${activeConnectorId || 'none'}-${idx}`}
                              cacheOptions
                              loadOptions={loadConnectorTickerOptions}
                              value={
                                row.symbol
                                  ? { value: row.symbol, label: row.symbol }
                                  : null
                              }
                              onChange={(opt) =>
                                updateStock(idx, { symbol: opt ? opt.value : null })
                              }
                              placeholder={
                                activeConnectorId
                                  ? 'Type to search ticker...'
                                  : 'Select a connector first'
                              }
                              isClearable
                              isDisabled={!activeConnectorId || saving}
                              defaultOptions={false}
                              noOptionsMessage={() =>
                                activeConnectorId ? 'No matches' : 'Select a connector first'
                              }
                            />
                          ) : (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.symbol}
                              readOnly
                              tabIndex={-1}
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          )}
                        </div>
                        <div style={{ width: 110 }}>
                          <div className="input-group input-group-sm">
                            <input
                              type="number"
                              className="form-control"
                              value={row.percentage}
                              min={0}
                              max={100}
                              step={0.1}
                              disabled={equalShare || saving || !row.symbol}
                              onChange={(e) => updateStock(idx, { percentage: e.target.value })}
                            />
                            <span className="input-group-text">%</span>
                          </div>
                        </div>
                        {isPickerRow ? (
                          <Button
                            color="secondary"
                            size="sm"
                            outline
                            type="button"
                            onClick={() => commitStockRow(idx)}
                            disabled={saving || !row.symbol}
                            title="Add stock"
                          >
                            + Add
                          </Button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeStock(idx)}
                            disabled={saving}
                            title="Remove"
                          >✕</button>
                        )}
                      </div>
                    );
                  })}

                  <div className="form-text mt-1">
                    Type a ticker into the last row, set its share, then click
                    "+ Add" to commit it. The row's ticker becomes read-only after
                    adding. Share % must sum to 100.
                  </div>
                </td>
              </tr>

              <tr>
                <th>Enabled</th>
                <td>
                  <FormGroup check inline className="mb-0">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={editEnabled}
                        onChange={(e) => setEditEnabled(e.target.checked)}
                        disabled={saving}
                      />
                      {' '}Enabled — only enabled strategies are processed by the trading engine.
                    </Label>
                  </FormGroup>
                </td>
              </tr>
              <tr>
                <th>Require Confirmation</th>
                <td>
                  <FormGroup check inline className="mb-0">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={editRequireTradeConfirmation}
                        onChange={(e) => setEditRequireTradeConfirmation(e.target.checked)}
                        disabled={saving}
                      />
                      {' '}Require manual confirmation before placing orders.
                    </Label>
                  </FormGroup>
                  <small className="text-muted d-block mt-1">
                    When enabled, the executor creates a pending trade entry instead of
                    placing orders immediately. You must approve or decline from the View page.
                  </small>
                </td>
              </tr>
              <tr>
                <th>Order Type</th>
                <td>
                  <div style={{ maxWidth: '280px' }}>
                    <Input
                      type="select"
                      value={editOrderType}
                      onChange={(e) => setEditOrderType(e.target.value)}
                      disabled={saving}
                    >
                      <option value="MarketOnClose">
                        Market-on-Close (fills at the closing auction)
                      </option>
                      <option value="Market">
                        Market (fills intraday at the next available price)
                      </option>
                    </Input>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Order type the executor posts for every leg (stock buys,
                    stock sells, money-market buys) when this strategy fires.
                    <br />
                    <strong>Market-on-Close</strong> is the historical default
                    and matches the close-of-day signal the model produced.
                    <br />
                    <strong>Market</strong> fills immediately intraday —
                    useful when you want the order to execute without
                    waiting for the close.
                  </small>
                </td>
              </tr>
              <tr>
                <th>Time of execution</th>
                <td>
                  <ExecutionTimeSlider
                    value={editExecutionTimeMinutes}
                    onChange={setEditExecutionTimeMinutes}
                    disabled={saving}
                  />
                </td>
              </tr>
              <tr>
                <th>Amount to use (USD)</th>
                <td>
                  <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <div style={{ width: '200px' }}>
                      <div className="input-group input-group-sm">
                        <input
                          type="number"
                          className="form-control"
                          value={editTradeAmountUsd}
                          min={0}
                          step={0.01}
                          disabled={saving}
                          onChange={(e) => setEditTradeAmountUsd(e.target.value)}
                          placeholder="Leave blank for all available cash"
                        />
                      </div>
                    </div>
                    <FormGroup check inline className="mb-0">
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={editAdjustTradeAmountAutomatically}
                          onChange={(e) => setEditAdjustTradeAmountAutomatically(e.target.checked)}
                          disabled={saving || editTradeAmountUsd === ''}
                        />
                        {' '}Adjust automatically
                      </Label>
                    </FormGroup>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Fixed USD budget used to size BUYs (stocks and money-market ticker).
                    Leave blank to deploy all available cash. When "Adjust automatically"
                    is checked, a daily scheduler updates this amount by the realised
                    profit/loss of recent round-trip orders.
                  </small>
                </td>
              </tr>
              {isAdmin && (
                <tr>
                  <th>Debugging</th>
                  <td>
                    <FormGroup check inline className="mb-0">
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={editEnableDebugging}
                          onChange={(e) => setEditEnableDebugging(e.target.checked)}
                          disabled={saving}
                        />
                        {' '}Enable debugging
                      </Label>
                    </FormGroup>
                    <small className="text-muted d-block mt-1">
                      When checked, this strategy is picked up by an admin's manual
                      "Trading Strategy Execution" trigger from the admin Schedulers
                      page — but only when the strategy is owned by the admin who
                      clicked the button. The daily 15:30 ET scheduler ignores this
                      flag and runs every enabled strategy regardless. Useful for
                      sanity-checking a single strategy end-to-end without firing
                      every enabled strategy in the database.
                    </small>
                  </td>
                </tr>
              )}
              <tr>
                <th></th>
                <td>
                  <div className="d-flex justify-content-end" style={{ gap: '8px' }}>
                    <Button
                      color="secondary"
                      tag={Link}
                      to="/trading-strategies"
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={
                        saving
                        || !editTradingConnectorId
                        || !editModelId
                        || !editSignalTag.trim()
                        || (committedStocks.length > 0 && Math.abs(percentSum - 100) >= 0.01)
                      }
                    >
                      {saving ? <Spinner size="sm" /> : (isCreating ? 'Create' : 'Save')}
                    </Button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </Form>
      )}
    </div>
  );
};

function emptyStockRow() {
  return { symbol: null, percentage: 0 };
}

// ── TimeSpan wire-format helpers ─────────────────────────────────────────────

// Converts an integer minutes-from-midnight value (e.g. 930 for 15:30) to the
// "HH:mm:ss" string that Newtonsoft.Json deserialises as TimeSpan on the server.
function minutesToTimeSpanString(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}

// Parses a Newtonsoft TimeSpan wire string ("HH:mm:ss") back to integer
// minutes-from-midnight for the slider. Returns `fallback` for null/undefined
// or any unrecognised format.
function timeSpanStringToMinutes(value, fallback) {
  if (!value || typeof value !== 'string') return fallback;
  const parts = value.split(':');
  if (parts.length < 2) return fallback;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return fallback;
  return h * 60 + m;
}

// ── Execution-time slider ────────────────────────────────────────────────────
//
// NY equity regular session: 09:30–16:00 ET.
// The 30-minute zone before close (15:30–16:00) is unavailable for scheduling
// because MOC orders must be submitted before Alpaca's 15:50 ET deadline.
// The slider therefore covers 09:30–15:30 ET (570–930 min from midnight).
// Step is 5 minutes for practical usability.

const EXEC_MIN = 9 * 60 + 30;   // 570  — 09:30 ET (market open)
const EXEC_MAX = 15 * 60 + 30;  // 930  — 15:30 ET (30 min before close)
const EXEC_STEP = 5;             // granularity in minutes

function formatEasternTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} ET`;
}

function ExecutionTimeSlider({ value, onChange, disabled }) {
  // Tick marks at notable times within the window.
  const ticks = [
    { minutes: 9 * 60 + 30,  label: '9:30' },
    { minutes: 11 * 60,       label: '11:00' },
    { minutes: 13 * 60,       label: '13:00' },
    { minutes: 15 * 60,       label: '15:00' },
    { minutes: 15 * 60 + 30, label: '15:30' },
  ];

  return (
    <div>
      <div className="d-flex align-items-center" style={{ gap: '12px' }}>
        {/* Slider + tick labels share the same flex-1 container so the
            percentage positions below are relative to the same width as
            the input element. */}
        <div style={{ flex: 1, position: 'relative', paddingBottom: '18px' }}>
          <input
            type="range"
            className="form-range"
            min={EXEC_MIN}
            max={EXEC_MAX}
            step={EXEC_STEP}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(Number(e.target.value))}
            style={{ width: '100%', marginBottom: 0 }}
          />

          {/* Tick labels — positioned inside the same container as the
              input so percentages map to the same pixel width.
              Bootstrap's form-range thumb is 1rem (16px); the track is
              inset by half that (8px) at each end. The calc() formula
              left: calc(P% + (8 - P*0.16)px) maps percentage P (0-100)
              to the correct pixel position regardless of container width:
                P=0   → left: 8px            (track start)
                P=50  → left: 50%            (centre)
                P=100 → left: calc(100% - 8px) (track end) */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              fontSize: '0.7rem',
              color: '#6c757d',
              userSelect: 'none',
            }}
          >
            {ticks.map((tick) => {
              const p = ((tick.minutes - EXEC_MIN) / (EXEC_MAX - EXEC_MIN)) * 100;
              const thumbHalf = 8; // half of Bootstrap's 1rem thumb
              return (
                <span
                  key={tick.minutes}
                  style={{
                    position: 'absolute',
                    left: `calc(${p}% + ${thumbHalf - p * thumbHalf / 50}px)`,
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tick.label}
                </span>
              );
            })}
          </div>
        </div>

        <span
          className="badge bg-primary"
          style={{ minWidth: '90px', fontSize: '0.85rem', textAlign: 'center' }}
        >
          {formatEasternTime(value)}
        </span>
      </div>

      <div style={{ marginTop: '30px' }}>
        <small className="text-muted">
          Time (Eastern) at which the scheduler triggers this strategy on trading days.
          The 15:30 – 16:00 ET zone is unavailable — MOC orders must be submitted
          before Alpaca's 15:50 ET deadline.
        </small>
      </div>
    </div>
  );
}
